import {
  DecisionEngineChannelConfig,
  DecisionEngineChannelType,
  DecisionEngineOutcomeReward,
  VariableDB,
  extractVariableValues,
} from "@hightouch/lib/customer-data/decision-engine/types";
import { generateDecisionEngineSyncConfig } from "@hightouch/core/server/graphql/schema/decision-engines/sync-config";
import {
  MailIcon,
  MobilePhoneIcon,
  ChatIcon,
  AlertIcon,
  GlobeIcon,
  JourneyIcon,
  SettingsIcon,
} from "@hightouch/htui";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { v4 as uuid } from "uuid";

import { useEntitlements } from "src/hooks/use-entitlement";

import { SyncOp, useTestSyncConfigurationQuery } from "src/graphql";
import { getRawModelRow } from "src/utils/models";
import { lowercase, snakecaseObject } from "src/utils/object";
import { processRequestInfo, type RequestInfo } from "src/utils/syncs";
import { getDestinationUI } from "./destination-ui";
import {
  isMessagePreviewDevShortCircuited,
  useMessagePreviewStateMachine,
} from "./use-message-preview-state";

export const ChannelOptions: Array<{
  label: string;
  value: DecisionEngineChannelType;
  icon: React.ComponentType;
}> = [
  {
    label: "Email",
    value: "email",
    icon: MailIcon,
  },
  {
    label: "SMS",
    value: "sms",
    icon: ChatIcon,
  },
  {
    label: "Push",
    value: "push",
    icon: AlertIcon,
  },
  {
    label: "In-App",
    value: "inapp",
    icon: MobilePhoneIcon,
  },
  {
    label: "Custom",
    value: "raw",
    icon: SettingsIcon,
  },
  {
    label: "Surface",
    value: "surface",
    icon: GlobeIcon,
  },
  {
    label: "Journey",
    value: "journey",
    icon: JourneyIcon,
  },
] as const;

export const ChannelOptionsMap = ChannelOptions.reduce(
  (acc, option) => ({ ...acc, [option.value]: option }),
  {} as Record<DecisionEngineChannelType, (typeof ChannelOptions)[number]>,
);

// Pull out variables that get a more custom treatment in the UI
export const parseMessageVariables = (variables: Array<VariableDB>) => {
  const subjectVar = variables?.find((variable) => variable.name === "subject");
  const titleVar = variables?.find((variable) => variable.name === "title");
  const bodyVar = variables?.find((variable) => variable.name === "body");

  return {
    subjects: subjectVar ? extractVariableValues(subjectVar) : undefined,
    titles: titleVar ? extractVariableValues(titleVar) : undefined,
    bodies: bodyVar ? extractVariableValues(bodyVar) : undefined,
    variables: variables?.filter(
      ({ name }) => name !== "subject" && name !== "title" && name !== "body",
    ),
  };
};

export const extractTestSendRequests = (
  batches: Array<{
    requestInfoSet?: Array<{
      requestType: string;
      data: Record<string, any>;
    } | null> | null;
  }>,
  definition: { name: string },
): RequestInfo[] =>
  batches
    .flatMap((batch) => batch.requestInfoSet ?? [])
    .filter((requestInfo) => requestInfo != null)
    .map((requestInfo) => processRequestInfo(requestInfo, definition));

export const useMessagePreview = ({
  channel,
  flowId,
  messageId,
}: {
  channel: {
    id: string;
    type: string;
    destination: { id: string; type: string; definition: { name: string } };
    config: DecisionEngineChannelConfig;
  };
  flowId: string;
  messageId?: string;
}) => {
  const client = useQueryClient();
  const [previewId, setPreviewId] = useState<string | undefined>();
  const [previewError, setPreviewError] = useState("");
  const [sendTestError, setSendTestError] = useState("");
  const [sendTestRequests, setSendTestRequests] = useState<RequestInfo[]>([]);

  const definition = getDestinationUI(
    channel.destination.type,
    channel.config,
    channel.type,
  );
  const isPreviewable = definition.isPreviewSupported;

  const stateMachineEnabled = Boolean(
    messageId &&
      isPreviewable &&
      channel.type === "email" &&
      definition.getEmailPreviewMethod === "e2e",
  );

  const stateMachine = useMessagePreviewStateMachine({
    messageId: messageId ?? "",
    // A short-circuited pane never renders state, so fetching it is waste.
    enabled: stateMachineEnabled && !isMessagePreviewDevShortCircuited(),
  });

  const run = async ({
    email,
    segment,
    workspaceId,
    campaignId,
    resourceId,
    user,
    items,
    variables,
    tags,
    sendToHightouchUser,
  }: {
    email?: string;
    segment: { id: string | number; columns: any };
    workspaceId: string;
    campaignId: string;
    resourceId: string;
    user: Record<string, string> | null | undefined;
    items: Record<string, any> | null | undefined;
    variables: Record<string, any> | null | undefined;
    tags?: Record<string, string> | null | undefined;
    // sendToHightouchUser is used to determine if the user should be sent to Hightouch preview user
    sendToHightouchUser: boolean;
  }) => {
    const previewId = uuid();
    const rawUser = user ? getRawModelRow(user, segment.columns) : {};

    const row = {
      hightouch_recommendation: {
        ...(items ? lowercase(snakecaseObject(items), 2) : {}),
        ...(variables ? { variables } : {}),
        ...(tags && Object.keys(tags).length ? { tags } : {}),
      },
      hightouch_user: lowercase(rawUser),
      campaign_id: campaignId,
      external_id:
        email || `${workspaceId}_${previewId}@decision-engine.hightouch.com`,
      id: previewId,
      // Static boolean is used for certain destinations like Braze
      // to make sure the user can be included in consent filters
      is_preview: true,
    };

    if (!sendToHightouchUser) {
      setSendTestRequests([]);
    }

    const response = await client.fetchQuery(previewId, {
      queryFn: useTestSyncConfigurationQuery.fetcher({
        segmentId: Number(segment.id),
        destinationId: Number(channel.destination.id),
        decisionEngineMessageId: messageId,
        syncConfig: generateDecisionEngineSyncConfig({
          flowId,
          channelId: channel.id,
          channelType: channel.type as any,
          destinationType: channel.destination.type as any,
          channelConfig: channel.config as DecisionEngineChannelConfig,
          campaignId,
          resourceId,
          isPreview: true,
          sendToHightouchUser,
          messageVariables: Object.keys(variables ?? {}).map((key) => ({
            name: key,
            variants: [],
          })),
        }),
        rows: [row],
        operation: SyncOp.Added,
      }),
    });

    if (isPreviewable) {
      setPreviewId(previewId);
    }

    if (!sendToHightouchUser) {
      setSendTestRequests(
        extractTestSendRequests(
          response.testSyncConfiguration.batches,
          channel.destination.definition,
        ),
      );
    }

    const rejection =
      response.testSyncConfiguration.batches[0]?.rejectedRows?.[0];
    const runError = response.testSyncConfiguration.error;

    if (rejection || runError) {
      const msg = runError || rejection?.reason || "Message failed to send";
      if (sendToHightouchUser) {
        setPreviewError(msg);
      } else {
        setSendTestError(msg);
      }
      return false;
    }

    if (!sendToHightouchUser) {
      setSendTestError("");
    }

    return true;
  };

  return {
    run,
    previewId,
    previewError,
    sendTestError,
    sendTestRequests,
    isPreviewable,
    stateMachineEnabled,
    stateMachine,
  };
};

export const sortGoals = (
  a: DecisionEngineOutcomeReward,
  b: DecisionEngineOutcomeReward,
  tieBreak?: () => number,
) => {
  // Sort by type first: "positive" comes before "negative"
  if (a.type === "positive" && b.type === "negative") {
    return -1;
  }
  if (a.type === "negative" && b.type === "positive") {
    return 1;
  }
  // If types are the same, sort by priority:
  if (a.type === b.type) {
    // If priorities are the same, sort by tie breaker
    if (a.priority === b.priority && tieBreak) {
      return tieBreak();
    }
    if (a.type === "positive") {
      // For positive, higher priority first
      return b.priority - a.priority;
    } else {
      // For negative, lower priority first
      return a.priority - b.priority;
    }
  }
  return 0;
};

export const useIsAiDecisionEngineEnabled = (): boolean => {
  const { data } = useEntitlements(true);
  return data.entitlements.decisionEngine;
};
